import { useMediaQuery } from '@mote/helpers/hooks'
import type { ImageProps } from '@mote/ui/components'
import { H2, H3, Image, Link, Text } from '@mote/ui/components'
import styled, { th } from '@xstyled/styled-components'
import { Container } from './shared'

type IUsage = {
  title: string
  description: string
  image: Pick<ImageProps, 'src' | 'alt' | 'sizes' | 'srcSet'>
  link: {
    to: string
    borderColor: string
  }
}

type IUsages = Array<IUsage>

const USAGES: IUsages = [
  {
    title: 'For feedback',
    description: 'Save time, demonstrate empathy and improve outcomes',
    link: {
      to: 'https://mote-com.webflow.io/use-cases/for-feedback',
      borderColor: '#cac0fa'
    },
    image: {
      src: 'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3).png',
      alt: 'Mote for feedback',
      sizes:
        '(max-width: 479px) 65vw, (max-width: 767px) 74vw, (max-width: 991px) 78vw, 39vw',
      srcSet:
        'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3)-p-500.png 500w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3)-p-800.png 800w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3)-p-1080.png 1080w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3)-p-1600.png 1600w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d5180cab269b0a2545012c_Feedback%20Hero%20(3).png 2220w'
    }
  },
  {
    title: 'For connection',
    description:
      'Promote creative thinking and empower personal expression through voice',
    link: {
      to: 'https://mote-com.webflow.io/use-cases/connect-with-all-learners',
      borderColor: '#cbeaf8'
    },
    image: {
      src: 'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3).png',
      alt: 'Mote for connection',
      sizes:
        '(max-width: 479px) 65vw, (max-width: 767px) 74vw, (max-width: 991px) 78vw, 39vw',
      srcSet:
        'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3)-p-500.png 500w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3)-p-800.png 800w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3)-p-1080.png 1080w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3)-p-1600.png 1600w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519cc8fd7ef31c2cfd543_Connection%20hero%20(3).png 2220w'
    }
  },
  {
    title: 'For diverse learners',
    description:
      'Nurture an inclusive learning culture whilst increasing confidence',
    link: {
      to: 'https://mote-com.webflow.io/use-cases/diverse-learners',
      borderColor: '#faccfd'
    },
    image: {
      src: 'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1).png',
      alt: 'Mote for diverse learners',
      sizes:
        '(max-width: 479px) 65vw, (max-width: 767px) 74vw, (max-width: 991px) 78vw, 39vw',
      srcSet:
        'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1)-p-500.png 500w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1)-p-800.png 800w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1)-p-1080.png 1080w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1)-p-1600.png 1600w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519d51b69720e070865a7_Accessibility%20hero%20(1).png 2220w'
    }
  },
  {
    title: 'For language learning',
    description:
      'Improve listening skills and build confidence with spoken word',
    link: {
      to: 'https://mote-com.webflow.io/use-cases/for-language-learning',
      borderColor: '#fbecce'
    },
    image: {
      src: 'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2).png',
      alt: 'Mote for language learning',
      sizes:
        '(max-width: 479px) 65vw, (max-width: 767px) 74vw, (max-width: 991px) 78vw, 39vw',
      srcSet:
        'https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2)-p-500.png 500w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2)-p-800.png 800w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2)-p-1080.png 1080w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2)-p-1600.png 1600w, https://uploads-ssl.webflow.com/626924b6f74c1a0a8a8b5414/62d519e149432b9d5619f0d8_Language%20Hero%20(2).png 2220w'
    }
  }
]

const Usage = (props: IUsage & { isReverse: boolean }): JSX.Element => {
  const image = (
    <UsageImageContainer>
      <UsageImage {...props.image} loading='lazy' />
    </UsageImageContainer>
  )
  const content = (
    <UsageContentContainer>
      <UsageContent>
        <UsageTitle>{props.title}</UsageTitle>
        <UsageDescription>{props.description}</UsageDescription>
        <UsageLearnMoreLink {...props.link}>Learn more</UsageLearnMoreLink>
      </UsageContent>
    </UsageContentContainer>
  )

  const ordered = !props.isReverse ? (
    <>
      {image}
      {content}
    </>
  ) : (
    <>
      {content}
      {image}
    </>
  )

  return <UsageRoot>{ordered}</UsageRoot>
}

export const UsagesSection = (): JSX.Element => {
  const breakpoint: number = useMediaQuery([767, 991, 1999])

  return (
    <Root>
      <UsagesContainer>
        <Title>Some handy ways to use mote</Title>
        {USAGES.map((usage, index) => (
          <Usage
            {...usage}
            key={index}
            isReverse={breakpoint > 991 && index % 2 !== 0}
          />
        ))}
      </UsagesContainer>
    </Root>
  )
}

const Root = styled(Container)`
  padding-top: 80px;
  padding-bottom: 40px;
  gap: 0;

  @media (max-width: 991px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`

const Title = styled(H2).attrs({
  alignment: 'center',
  lineHeight: '123.5%',
  fontWeight: 400,
  color: 'black'
})`
  width: 80%;
  font-size: 39px;
  padding-top: 40px;
  padding-bottom: 20px;

  @media (max-width: 991px) {
    font-size: 32px;
    padding-top: 20px;
    padding-bottom: 10px;
  }
`

const UsagesContainer = styled.div`
  display: flex;
  width: 90%;
  max-width: 1400px;
  padding-top: 80px;
  padding-bottom: 80px;
  flex-direction: column;
  align-items: center;
  border-radius: 24px;
  background-color: ${th.color('grey.l4')};

  @media (max-width: 991px) {
    padding-top: 60px;
    padding-bottom: 60px;
  }

  @media (max-width: 767px) {
    padding-top: 40px;
    padding-bottom: 40px;
  }

  @media (max-width: 479px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`

const UsageRoot = styled.div`
  display: flex;
  width: 100%;
  padding: 60px;

  &:first-of-type {
    margin-top: 40px;
  }

  @media (max-width: 991px) {
    padding: 20px 40px;
    flex-direction: column;
  }

  @media (max-width: 767px) {
    padding-right: 20px;
    padding-left: 20px;
  }
`

const UsageContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
    align-items: flex-start;
    justify-content: flex-start;
  }
`

const UsageContent = styled.div`
  width: 70%;

  @media (max-width: 991px) {
    width: 100%;
  }
`

const UsageImageContainer = styled.div`
  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
  }
`

const UsageImage = styled(Image)`
  border-radius: 16px;
  max-width: 100%;
  margin-bottom: 16px;
  vertical-align: middle;
`

const UsageTitle = styled(H3).attrs({
  color: 'black',
  lineHeight: '44px',
  fontWeight: 400
})`
  font-size: 32px;
  margin-top: 20px;
  margin-bottom: 10px;

  @media (max-width: 479px) {
    font-size: 22px;
    margin: 0;
  }
`

const UsageDescription = styled(Text).attrs({
  color: 'grey.d2',
  lineHeight: '150%'
})`
  margin-bottom: 0px;
  padding-top: 16px;
  padding-bottom: 16px;
  font-size: 18px;
  max-width: 400px;

  @media (max-width: 991px) {
    max-width: 600px;
  }
`

const UsageLearnMoreLink = styled(Link).attrs({
  color: 'black',
  lineHeight: '48px',
  fontWeight: 500,
  borderStyle: 'solid',
  borderBottomWidth: '3px'
})`
  display: inline-block;
  height: 48px;
  font-size: 18px;
  padding: 8px 8px 4px;
  transition: all 300ms ease;

  &:hover,
  &:focus,
  &:active {
    color: black;
    border-bottom-width: 16px;
  }

  @media (max-width: 479px) {
    font-size: 16px;
  }
`
